import { httpClient } from '@bs/core'

interface FileParams {
  parent_id?: string
  name: string
  file_type: string
  content_type: string
  width?: number
  height?: number
  size: number
  alternative_text?: string
  title?: string
  caption?: string
  description?: string
  tags?: string[]
  asset_types?: string[]
}

interface NotifyUploadedParams {
  file_id: string
}

export interface ListFilesFilters {
  search_parameter?: string
  content_type?: string
  asset_type?: string
}

interface ListFilesParams {
  folder_id?: string
  limit: number
  page: number
  sort?: Record<string, 1 | -1>
  filters?: ListFilesFilters
}

interface ListFoldersParams {
  // Folder params if needed
}

export interface GalleryFile {
  _id?: string
  id?: unknown
  id_select?: unknown
  parent_id?: string | null
  name?: string
  file_type?: string
  src?: string
  public_url?: string
  thumbnail?: string
  alternative_text?: string
  alt?: string
  title?: string
  caption?: string
  description?: string
  content_type?: string
  size?: number
  width?: number
  height?: number
  blob_name?: string
  copy_link?: string | null
  srcset?: Record<string, string>
  tags?: string[]
  asset_types?: string[]
  created_at?: string
  _created?: string
  _updated?: string
  operator_id?: string
  organization_id?: string
  resumable_upload_session?: string
  // Set only on a Breadstack catalog image nobody has copied yet (product picker).
  // Picking it copies it into Breadstack first; see copySpatulaImage.
  spatula_doc_id?: string
  spatula_image_url?: string
}

export interface GalleryFolder {
  id: string
  name: string
  count: number
  level?: number
  parent_id?: string | number
  droppable?: boolean
  paths?: string[]
}

export interface MediaStats {
  total_items: number
  banner: number
  brand_logo: number
  category: number
  product_image: number
  gallery: number
  video: number
}

interface ListFilesResponse {
  files: GalleryFile[]
  total_pages: number
  page: number
  stats: MediaStats
}

interface ApiListFilesResponse {
  items: GalleryFile[]
  limit: number
  page: number
  total_items: number
  total_pages: number
  banner?: number
  brand_logo?: number
  category?: number
  product_image?: number
  gallery?: number
  video?: number
}

interface ListFoldersResponse {
  folders: GalleryFolder[]
  unsortedFileCount?: number
}

interface ApiListFoldersResponse {
  items?: GalleryFolder[]
  data?: GalleryFolder[]
  folders?: GalleryFolder[]
  unsorted_file_count?: number
  unsortedFileCount?: number
}

export interface UpdateFileParams {
  file_id: string
  name?: string
  alternative_text?: string
  title?: string
  caption?: string
  description?: string
  tags?: string[]
  asset_types?: string[]
}

export interface BulkUpdateAssetTypesParams {
  // Mongo _ids of the tenant-owned files to update. Public/other-org files are
  // never targeted here — they don't belong to this organization and the backend
  // scopes the write by organization_id.
  media_id_list: string[]
  // Replace semantics: each listed file's asset_types becomes exactly this set.
  // An empty array clears all asset types on the listed files.
  asset_types: string[]
}

export interface SpatulaImageParams {
  store_id: string
  organization_id: string
  spatula_doc_id: string
  image_url: string
}

// 'ready' carries the copied image as an ordinary Breadstack (public-org) file.
export interface SpatulaImageStatus {
  status: 'ready' | 'pending'
  file?: GalleryFile
}

interface PublicListFilesParams {
  store_id: string
  organization_id: string
  limit: number
  page: number
  filters?: ListFilesFilters
}

const ACTIONS = {
  PAGINATE_FILES: 'paginate_files',
  PAGINATE_PUBLIC_FILES: 'paginate_public_files',
  CREATE_FILE: 'create_file',
  NOTIFY_UPLOADED: 'notify_uploaded',
  DELETE_FILE: 'delete_file',
  CREATE_FOLDER: 'create_folder',
  READ_FOLDER: 'read_folder',
  DELETE_FOLDER: 'delete_folder',
  UPDATE_FILE: 'update_file',
  UPDATE_FOLDER: 'update_folder',
  GET_FOLDERS_STRUCTURE: 'get_folders_structure',
  MOVE_MEDIA: 'move_media',
  BULK_UPDATE_ASSET_TYPES: 'bulk_update_asset_types',
  PAGINATE_SPATULA_CATALOG: 'paginate_spatula_catalog',
  COPY_SPATULA_IMAGE: 'copy_spatula_image',
  GET_SPATULA_IMAGE: 'get_spatula_image',
}

const url = {
  READ: '/stores/media/',
}

export const galleryApis = {
  getFiles: async (params: ListFilesParams): Promise<ListFilesResponse> => {
    const normalizedParams: Record<string, unknown> = {
      folder_id: params.folder_id === 'all' || params.folder_id === 'unsorted' ? '' : params.folder_id,
      limit: params.limit,
      page: params.page,
      sort: params.sort ?? { _id: -1 },
    }

    if (params.filters) {
      const filters: ListFilesFilters = {}
      if (params.filters.search_parameter) {
        filters.search_parameter = params.filters.search_parameter
      }
      if (params.filters.content_type) {
        filters.content_type = params.filters.content_type
      }
      if (params.filters.asset_type) {
        filters.asset_type = params.filters.asset_type
      }
      if (Object.keys(filters).length > 0) {
        normalizedParams.filters = filters
      }
    }

    const response = (await httpClient.json.post(url.READ, {
      action: ACTIONS.PAGINATE_FILES,
      parameters: normalizedParams,
    })) as ApiListFilesResponse

    // console.log('[GalleryAPI] getFiles response:', response)

    return {
      files: response.items || [],
      total_pages: response.total_pages || 0,
      page: response.page || 1,
      stats: {
        total_items: response.total_items || 0,
        banner: response.banner || 0,
        brand_logo: response.brand_logo || 0,
        category: response.category || 0,
        product_image: response.product_image || 0,
        gallery: response.gallery || 0,
        video: response.video || 0,
      },
    }
  },

  getFolders: async (params?: ListFoldersParams): Promise<ListFoldersResponse> => {
    const response = (await httpClient.json.post(url.READ, {
      action: ACTIONS.GET_FOLDERS_STRUCTURE,
      parameters: params || {},
    })) as ApiListFoldersResponse

    const folders = response.items || response.data || response.folders || []
    const unsortedFileCount = response.unsorted_file_count || response.unsortedFileCount || 0

    return {
      folders,
      unsortedFileCount,
    }
  },

  createFile: async (params: FileParams): Promise<GalleryFile> => {
    const response = await httpClient.json.post(url.READ, {
      action: ACTIONS.CREATE_FILE,
      parameters: params,
    })
    return response as GalleryFile
  },

  notifyUploaded: async (params: NotifyUploadedParams): Promise<GalleryFile> => {
    const response = await httpClient.json.post(url.READ, {
      action: ACTIONS.NOTIFY_UPLOADED,
      parameters: params,
    })
    return response as GalleryFile
  },

  deleteFile: async (params: { file_id: string }): Promise<void> => {
    await httpClient.json.post(url.READ, {
      action: ACTIONS.DELETE_FILE,
      parameters: params,
    })
  },

  createFolder: async (params: Record<string, unknown>): Promise<GalleryFolder> => {
    const response = await httpClient.json.post(url.READ, {
      action: ACTIONS.CREATE_FOLDER,
      parameters: params,
    })
    return response as GalleryFolder
  },

  deleteFolder: async (params: { folder_id: string }): Promise<void> => {
    await httpClient.json.post(url.READ, {
      action: ACTIONS.DELETE_FOLDER,
      parameters: params,
    })
  },

  updateFile: async (params: UpdateFileParams): Promise<GalleryFile> => {
    const response = await httpClient.json.post(url.READ, {
      action: ACTIONS.UPDATE_FILE,
      parameters: params,
    })
    return response as GalleryFile
  },

  // Replace asset_types across many tenant-owned files in a single request.
  // Backend action `bulk_update_asset_types` reuses blob_qs.update_many_blobs
  // ($set asset_types, $in on _id, scoped by organization_id). The response
  // shape (e.g. `{ updated: <count> }`) is not consumed — only HTTP success matters.
  bulkUpdateAssetTypes: async (params: BulkUpdateAssetTypesParams): Promise<void> => {
    await httpClient.json.post(url.READ, {
      action: ACTIONS.BULK_UPDATE_ASSET_TYPES,
      parameters: params,
    })
  },

  // Product picker only: Breadstack catalog images not copied into Breadstack yet.
  // Needs search text; the backend returns nothing for non-product-image filters.
  getSpatulaCatalogFiles: async (params: PublicListFilesParams): Promise<ListFilesResponse> => {
    const response = (await httpClient.json.post(url.READ, {
      store_id: params.store_id,
      organization_id: params.organization_id,
      action: ACTIONS.PAGINATE_SPATULA_CATALOG,
      parameters: {
        limit: params.limit,
        page: params.page,
        search_parameter: params.filters?.search_parameter ?? '',
        asset_type: params.filters?.asset_type ?? '',
        content_type: params.filters?.content_type ?? '',
      },
    })) as ApiListFilesResponse

    return {
      files: response.items || [],
      total_pages: response.total_pages || 0,
      page: response.page || 1,
      stats: {
        total_items: response.total_items || 0,
        banner: 0,
        brand_logo: 0,
        category: 0,
        product_image: 0,
        gallery: 0,
        video: 0,
      },
    }
  },

  // Starts copying a picked catalog image into Breadstack (runs in the worker).
  copySpatulaImage: async (params: SpatulaImageParams): Promise<SpatulaImageStatus> => {
    const { store_id, organization_id, ...parameters } = params
    return (await httpClient.json.post(url.READ, {
      store_id,
      organization_id,
      action: ACTIONS.COPY_SPATULA_IMAGE,
      parameters,
    })) as SpatulaImageStatus
  },

  // Polled after copySpatulaImage until the copy is ready.
  getSpatulaImage: async (params: SpatulaImageParams): Promise<SpatulaImageStatus> => {
    const { store_id, organization_id, ...parameters } = params
    return (await httpClient.json.post(url.READ, {
      store_id,
      organization_id,
      action: ACTIONS.GET_SPATULA_IMAGE,
      parameters,
    })) as SpatulaImageStatus
  },

  getPublicFiles: async (params: PublicListFilesParams): Promise<ListFilesResponse> => {
    const response = (await httpClient.json.post(url.READ, {
      store_id: params.store_id,
      organization_id: params.organization_id,
      action: ACTIONS.PAGINATE_PUBLIC_FILES,
      parameters: {
        limit: params.limit,
        page: params.page,
        filters: params.filters ?? {},
      },
    })) as ApiListFilesResponse

    return {
      files: response.items || [],
      total_pages: response.total_pages || 0,
      page: response.page || 1,
      stats: {
        total_items: response.total_items || 0,
        banner: response.banner || 0,
        brand_logo: response.brand_logo || 0,
        category: response.category || 0,
        product_image: response.product_image || 0,
        gallery: response.gallery || 0,
        video: response.video || 0,
      },
    }
  },
}
